/**
 * PWA Service Worker Registration and Update Management
 * 
 * This module handles:
 * - Service worker registration with vite-plugin-pwa
 * - Update detection and confirmation
 * - Custom event dispatching for UI updates
 */

/**
 * Initialize PWA service worker registration
 * Configures auto-update behavior and dispatches events when updates are available
 */
export function initPWA() {
  // Skip PWA in development
  if (import.meta.env.DEV) {
    console.debug('[PWA] Skipped in development mode');
    return;
  }

  // Only run in production
  if (!import.meta.env.PROD) {
    return;
  }

  // Dynamically import the registration module only in production
  // This avoids Vite trying to resolve the virtual module in development
  import('./pwa-register')
    .then(({ registerPWA }) => {
      registerPWA();
    })
    .catch(error => {
      console.debug('[PWA] Registration module not available:', error);
    });
}

/**
 * Confirm app update - triggers service worker update and page reload
 * This should be called when user clicks "Update now" in the UI
 */
export async function confirmUpdate() {
  // Try to get the update function from the global scope
  const updateSW = (window as any).__pwaUpdate;
  
  if (!updateSW) {
    console.warn('[PWA] No update function available');
    // Fallback to simple reload
    window.location.reload();
    return;
  }

  try {
    // This will skip waiting on the new SW and reload the page
    await updateSW(true);
  } catch (error) {
    console.error('[PWA] Update failed:', error);
    // Fallback to simple reload
    window.location.reload();
  }
}

/**
 * Check if service worker is supported and registered
 */
export function isPWAReady(): boolean {
  return 'serviceWorker' in navigator && (window as any).__pwaUpdate !== undefined;
}